import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import api from "../utils/api";
import DataGate from "../components/DataGate";
import Toast from "../components/ui/Toast";
import { newId } from "../inventory/parse";

// Media plans: channels picked from the inventory, with a budget per line.
// Kept by the API (inventory-api), shared by everyone.
//
// A plan:
//   { id, name, client, status, budgets: { EUR: 30000, TRY: 500000, ... },
//     startDate, endDate, notes,
//     items: [{ channelId, budget, cpm?, snapshot }],
//     createdAt, createdBy, updatedAt, updatedBy }
// `snapshot` keeps the channel's name, partner and price from when it was
// added, so the plan still reads if the channel is later deleted. `cpm` on a
// line is a negotiated price; empty means the channel's list CPM.
//
// The plan screen changes a plan on every keystroke, so changed plans are
// sent whole once typing pauses (SAVE_DELAY), one request at a time.

export const PLAN_STATUSES = ["Draft", "Sent", "Won", "Lost"];

const SAVE_DELAY = 500;

const PlansContext = createContext(null);

const snapshotOf = (c) => ({
  channelName: c.channelName,
  partner: c.partner,
  territories: c.territories,
  formats: c.formats,
  cpm: c.cpm,
  currency: c.currency,
  reach: c.reach,
});

export function PlansProvider({ children }) {
  const { user } = useAuth();
  const who = user?.name || user?.email || "unknown";
  const [plans, setPlans] = useState([]);
  const [status, setStatus] = useState("loading"); // loading | ready | failed
  const [syncError, setSyncError] = useState(null);
  const clearSyncError = useCallback(() => setSyncError(null), []);

  const reload = useCallback(async () => {
    try {
      const res = await api.get("/plans");
      setPlans(res.data);
      setStatus("ready");
    } catch {
      setStatus((s) => (s === "ready" ? s : "failed"));
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const queue = useRef(Promise.resolve());
  const send = useCallback(
    (request) => {
      queue.current = queue.current.then(request).catch(() => {
        setSyncError({ message: "A plan change could not be saved. Showing what the server has." });
        reload();
      });
    },
    [reload]
  );

  // Plans changed since the last save, sent once SAVE_DELAY passes quietly
  const dirty = useRef(new Set());
  const latest = useRef(plans);
  latest.current = plans;

  useEffect(() => {
    if (!dirty.current.size) return undefined;
    const t = setTimeout(() => {
      const ids = [...dirty.current];
      dirty.current.clear();
      ids.forEach((id) => {
        const plan = latest.current.find((p) => p.id === id);
        if (plan) send(() => api.put(`/plans/${encodeURIComponent(id)}`, plan));
      });
    }, SAVE_DELAY);
    return () => clearTimeout(t);
  }, [plans, send]);

  const touch = useCallback(
    (plan) => ({ ...plan, updatedAt: Date.now(), updatedBy: who }),
    [who]
  );

  const change = useCallback(
    (id, fn) => {
      dirty.current.add(id);
      setPlans((list) => list.map((p) => (p.id === id ? touch(fn(p)) : p)));
    },
    [touch]
  );

  // channels: full channel objects to start the plan with
  const createPlan = useCallback(
    ({ name, channels = [] } = {}) => {
      const at = Date.now();
      const plan = {
        id: newId(),
        name: name || `New plan ${new Date(at).toLocaleDateString("en-GB")}`,
        client: "",
        status: "Draft",
        budgets: {},
        startDate: "",
        endDate: "",
        notes: "",
        items: channels.map((c) => ({ channelId: c.id, budget: 0, snapshot: snapshotOf(c) })),
        createdAt: at,
        createdBy: who,
        updatedAt: at,
        updatedBy: who,
      };
      dirty.current.add(plan.id);
      setPlans((list) => [plan, ...list]);
      return plan;
    },
    [who]
  );

  const updatePlan = useCallback((id, patch) => change(id, (p) => ({ ...p, ...patch })), [change]);

  const deletePlan = useCallback(
    (id) => {
      dirty.current.delete(id);
      setPlans((list) => list.filter((p) => p.id !== id));
      send(() => api.delete(`/plans/${encodeURIComponent(id)}`));
    },
    [send]
  );

  const duplicatePlan = useCallback(
    (id) => {
      const source = plans.find((p) => p.id === id);
      if (!source) return null;
      const at = Date.now();
      const copy = {
        ...source,
        id: newId(),
        name: `${source.name} (copy)`,
        status: "Draft",
        items: source.items.map((i) => ({ ...i })),
        createdAt: at,
        createdBy: who,
        updatedAt: at,
        updatedBy: who,
      };
      dirty.current.add(copy.id);
      setPlans((list) => [copy, ...list]);
      return copy;
    },
    [plans, who]
  );

  // Adds channels not already in the plan; returns how many were new
  const addChannels = useCallback(
    (id, channels) => {
      const plan = plans.find((p) => p.id === id);
      if (!plan) return 0;
      const have = new Set(plan.items.map((i) => i.channelId));
      const fresh = channels.filter((c) => !have.has(c.id));
      if (fresh.length) {
        change(id, (p) => ({
          ...p,
          items: [
            ...p.items,
            ...fresh.map((c) => ({ channelId: c.id, budget: 0, snapshot: snapshotOf(c) })),
          ],
        }));
      }
      return fresh.length;
    },
    [plans, change]
  );

  const updateLine = useCallback(
    (id, channelId, patch) =>
      change(id, (p) => ({
        ...p,
        items: p.items.map((i) => (i.channelId === channelId ? { ...i, ...patch } : i)),
      })),
    [change]
  );

  const removeLine = useCallback(
    (id, channelId) =>
      change(id, (p) => ({ ...p, items: p.items.filter((i) => i.channelId !== channelId) })),
    [change]
  );

  const setItems = useCallback((id, items) => change(id, (p) => ({ ...p, items })), [change]);

  return (
    <PlansContext.Provider
      value={{
        plans,
        reload,
        createPlan,
        updatePlan,
        deletePlan,
        duplicatePlan,
        addChannels,
        updateLine,
        removeLine,
        setItems,
      }}
    >
      <DataGate status={status} what="media plans" onRetry={reload}>
        {children}
      </DataGate>
      <Toast toast={syncError} onDone={clearSyncError} />
    </PlansContext.Provider>
  );
}

export function usePlans() {
  return useContext(PlansContext);
}
